
import { defineComponent, ref, computed, getCurrentInstance, PropType, onMounted, onUnmounted, watch } from 'vue';
import KwaiPlayer from '@ks-video/kwai-player-web/full';
import Cookies from 'js-cookie';
import UserInfoItem from '../user-info-item/index.vue';
import CaptionItem from '../caption-item/index.vue';
import PhotoActionItem from '../video-action-item/index.vue';
import { getLlsidFromExptag } from '@/common/common-params';
import { sendLog, radarLog } from '~/common/radar';
import Loading from '@/components/loading/index.vue';
import CommentDialog from '@/components/comment-dialog/seo-index.vue';
import { MobilePageSource } from '@/constants/base';
import { Feed } from '~/api/feed';
import { useCommentDialog } from '~/components/comment-dialog/hooks/useCommentDialog';
import { UserInfoByLogin } from '@/api/user';
import { FeedPageEnum } from '@/store/modules/feed';

type VideoRef = KwaiPlayer & {
    getCurrentTime?: () => number | undefined;
    setCurrentTime?: (time: number) => void;
    getDuration?: () => number | undefined;
};

export default defineComponent({
    components: {
        CaptionItem,
        Loading,
        UserInfoItem,
        PhotoActionItem,
        CommentDialog,
    },
    props: {
        type: {
            type: String,
            default: () => '',
        },
        muted: {
            type: Boolean,
            default: true,
        },
        isLoginStatus: {
            type: Boolean,
            default: false,
        },
        index: {
            type: Number,
            default: 0,
        },
        feed: {
            type: Object as PropType<Feed>,
            default: () => ({}),
        },
        discoverCurrentIndex: {
            type: Number,
            default: 0,
        },
    },
    emits: ['onTargetPhotoFocused'],
    setup(props, { emit }) {
        const { proxy } = getCurrentInstance() as { proxy: any };

        const videoRef = ref<VideoRef>();
        const videoCardRef = ref<HTMLElement>();
        const isPlaying = ref(false);
        const isLoading = ref(false);
        const canPlay = ref(false);
        const imageLoaded = ref(false);
        let cardIntersectionObserver: IntersectionObserver;
        const focusCurrentVideo = ref(false);

        const userInfoByLogin = computed<UserInfoByLogin>(() => {
            const userInfoByLogin = (proxy?.$store?.state?.userModule?.userInfo || {}) as UserInfoByLogin;
            return userInfoByLogin;
        });

        const showVideoDom = computed(() => {
            const diff = props.index - props.discoverCurrentIndex;
            // index == 0时，只渲染第0个卡片
            // 不为0时，渲染 当前位置前2加后5卡片
            const isShow = (props.discoverCurrentIndex === 0 ? diff <= 0 : diff <= 5 && diff >= -2) || focusCurrentVideo.value;

            return isShow;
        });

        watch(
            () => showVideoDom,
            newVal => {
                if (!newVal) {
                    imageLoaded.value = false;
                }
            }
        );

        const { commentLists, commentDialogVisable, showCommentLoading, fetchSeoComment } = useCommentDialog();

        onMounted(() => {
            const viewLayoutRects: any = document.querySelector('.main')!.getClientRects();
            cardIntersectionObserver = new IntersectionObserver(
                entries => {
                    entries.forEach(entry => {
                        const top = entry.boundingClientRect.top;
                        const isInBottom = entry.boundingClientRect.bottom - viewLayoutRects.height > 0;
                        console.log(
                            'handleIntersection',
                            entry,
                            props.index,
                            entry.isIntersecting,
                            entry.boundingClientRect.top,
                            entry.intersectionRatio,
                            isInBottom
                        );

                        focusCurrentVideo.value = entry.isIntersecting;

                        if (entry.isIntersecting && top > 0 && entry.intersectionRatio > 0.8 && !isInBottom) {
                            setVideoPlay();
                        } else {
                            setVideoPause();
                        }
                    });
                },
                {
                    threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
                }
            );

            videoCardRef.value && cardIntersectionObserver.observe(videoCardRef.value);
        });

        onUnmounted(() => {
            cardIntersectionObserver?.disconnect?.();
        });

        const cardContainerStyle = computed(() => {
            const width = props.feed?.ext_params?.w || 0;
            const height = props.feed?.ext_params?.h || 0;

            // 第一个卡片高度强制为125vw，不进行压扁、裁剪
            // 原因：保证第一个卡片为屏幕上最大元素，LCP计算时采用第一个卡片渲染完成作为终点
            if (height <= 0 || width <= 0 || props.index === 0) {
                return {
                    width: '100%',
                    height: '125vw',
                };
            }

            return {
                width: '100%',
                height: height / width >= 1.25 ? '125vw' : `${(height / width) * 100}vw`,
            };
        });

        const cardCommonParams = computed(() => {
            return {
                card_type: 'photo_card',
                card_position: props.index,
            };
        });

        const onPlayOrPauseBtnClick = () => {
            if (isPlaying.value) {
                sendLog({
                    type: 'click',
                    name: 'PHOTO_CARD_SPECIAL',
                    value: {
                        button_type: 'pause',
                        ...cardCommonParams.value,
                    },
                });
                isPlaying.value = false;
                setVideoPause();
            } else {
                sendLog({
                    type: 'click',
                    name: 'PHOTO_CARD_SPECIAL',
                    value: {
                        button_type: 'play',
                        ...cardCommonParams.value,
                    },
                });
                isPlaying.value = true;
                setVideoPlay();
            }
        };

        const setVideoPlay = () => {
            videoRef?.value?.play?.();
            emit('onTargetPhotoFocused', props.index);
        };

        const setVideoPause = () => {
            videoRef?.value?.pause?.();
        };

        const kwaiId = computed(() => {
            return props.feed?.kwai_id || '';
        });

        const caption = computed(() => {
            const caption = props.feed?.caption;
            return caption && caption.length > 5 ? caption : '';
        });

        const photoId = computed(() => {
            return props.feed?.photo_id_str || '';
        });

        const onAvatarOrNameClick = () => {
            sendLog({
                type: 'click',
                name: 'PHOTO_CARD_SPECIAL',
                value: {
                    button_type: 'author_icon',
                    ...cardCommonParams.value,
                },
            });
            proxy?.$router?.push({ path: `/@${kwaiId.value}`, query: { page_source: 'new_discover' } });
        };

        const handleVideoClick = () => {
            sendLog({
                type: 'click',
                name: 'PHOTO_COVER',
                value: {
                    ...logParams.value,
                },
            });

            const pathname = window?.location?.pathname || '';
            const searchWord = decodeURIComponent(pathname.split('/')?.[2] || '');
            // 由于 feed-list 组件不会清空 discoverSearch 的 feed 数据，所以在这里先清空 discoverSearch 的 feed 数据
            // 保证跳转过去后能够正确的请求数据
            proxy?.$store?.commit?.('feedMoudle/resetFeedList', FeedPageEnum.DiscoverSearch);
            proxy?.$router?.push({
                path: `/search/${searchWord}${searchWord ? '/' : ''}video/${photoId.value}`,
                query: { page_source: 'new_discover' },
            });
        };

        const handleOnPlay = () => {
            isLoading.value = true;
        };

        const handlePlaying = () => {
            isPlaying.value = true;
            isLoading.value = false;
        };

        const handleCanPlay = (index: number) => {
            canPlay.value = true;
        };

        const handlePlayEnd = () => {
            isPlaying.value = false;
        };

        const handlePause = () => {
            isPlaying.value = false;
            sendPlayTimeLog(0);
        };

        const handleWaiting = () => {};

        const handleError = (e: any) => {
            radarLog({
                name: 'PWA_VIDEO_ERROR_IN_DISCOVER',
                event_type: 'click',
                category: 'KWAI_SHARE',
                message: `error photo: ${photoId.value}`,
                src: window.location.pathname,
            });
            sendPlayTimeLog(1);
        };

        const sendPlayTimeLog = (ifError: number) => {
            const { user, userId, user_id } = userInfoByLogin.value || {};
            const uid = userId || user_id || user?.user_id || Cookies.get('userId');

            const playTime = videoRef?.value?.getCurrentTime?.() || 0;
            const duration = videoRef?.value?.getDuration?.() || 0;
            if (playTime > 0 && photoId.value && duration > 0) {
                sendLog({
                    type: 'click',
                    name: 'PLAY_TIME_MOBILE',
                    value: {
                        user_id: uid,
                        kwai_id: user?.kwai_id,
                        author_kwai_id: props?.feed?.kwai_id,
                        author_id: props?.feed?.user_id_str || props?.feed?.user_id,
                        photo_id: photoId.value,
                        play_time: `${Math.ceil(playTime) * 1000}`,
                        photo_time: `${Math.ceil(duration) * 1000}`,
                        if_error: ifError,
                        exp_tag: props?.feed?.exp_tag,
                        llsid: getLlsidFromExptag(props?.feed?.exp_tag),
                    },
                });
            }
        };

        const logParams = computed(() => {
            return {
                author_id: props.feed?.user_id || props.feed?.kwai_id,
                photo_id: photoId.value,
                like: props.feed?.like_count,
                play: props.feed?.view_count,
                comment: props.feed?.comment_count,
                share: props.feed?.forward_count,
                card_type: 'photo_card',
            };
        });

        const onMutedIconClick = () => {
            proxy.$bus.emit('setMuted', false);
        };

        const onCommentUserNameClick = (kwaiId?: string) => {
            proxy?.$router?.push({ path: `/@${kwaiId ?? ''}`, query: { page_source: 'new_discover' } });
        };

        const onImageLoad = () => {
            imageLoaded.value = true;
        };

        const handleCommentClick = () => {
            commentDialogVisable.value = true;
            fetchSeoComment(photoId.value);
        };

        const sendActionRecoLog = (actionName: string) => {
            const { user, userId, user_id } = userInfoByLogin.value || {};
            const uid = userId || user_id || user?.user_id || Cookies.get('userId');

            sendLog({
                type: 'click',
                name: actionName,
                value: {
                    user_id: uid,
                    kwai_id: user?.kwai_id,
                    author_id: props.feed?.user_id_str || props.feed?.user_id,
                    author_kwai_id: props?.feed?.kwai_id,
                    play_time: `${Math.ceil(videoRef?.value?.getCurrentTime?.() || 0) * 1000}`,
                    duration: `${Math.ceil(videoRef?.value?.getDuration?.() || 0) * 1000}`,
                    identity: props.feed?.photo_id_str,
                    index: props.index,
                    llsid: getLlsidFromExptag(props?.feed?.exp_tag),
                    exp_tag: props?.feed?.exp_tag,
                    type: 'PHOTO',
                    photo_id: photoId.value,
                },
            });
        };

        return {
            videoRef,
            photoId,
            isPlaying,
            caption,
            logParams,
            isLoading,
            canPlay,
            videoCardRef,
            cardContainerStyle,
            cardCommonParams,
            bizData: { pageSource: MobilePageSource.PwaDiscoverCategoryCard },
            imageLoaded,
            showVideoDom,
            commentLists,
            commentDialogVisable,
            showCommentLoading,

            handlePause,
            handlePlayEnd,
            handleVideoClick,
            onPlayOrPauseBtnClick,
            handlePlaying,
            handleError,
            handleWaiting,
            handleOnPlay,
            handleCanPlay,
            onMutedIconClick,
            onCommentUserNameClick,
            onAvatarOrNameClick,
            onImageLoad,
            fetchSeoComment,
            handleCommentClick,
            sendActionRecoLog,
        };
    },
});
